import { useEffect, useRef, useState } from 'react';
import DocumentScanner from './DocumentScanner.jsx';
import DocViewer, { detectKind } from './DocViewer.jsx';
import { compressDataUrl } from '../utils/image.js';
import { api } from '../utils/api.js';
import { friendlyError } from '../utils/errors.js';
import { extractPdfPages, renderPdfPageImages } from '../utils/pdfText.js';
import ManualLibraryLink from './ManualLibraryLink.jsx';

const PROGRESS_UPLOAD = ['Uploading file…', 'Reading content…', 'Indexing for AI…'];
const PROGRESS_SCAN   = ['Capturing image…', 'Reading tech sheet…', 'Extracting test points…', 'Saving for future jobs…'];

export default function DocumentManager({ modelNumber, lookupId, onDocumentsChange, allowLink = false }) {
  const [documents, setDocuments] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  // busy: 'upload-tech_sheet' | 'upload-wiring_diagram' | 'scan' | null
  const [busy, setBusy] = useState(null);
  const [stage, setStage] = useState(0);
  const [error, setError] = useState(null);
  // What a failed upload or scan sent ({ kind: "upload", file, docType } or
  // { kind: "scan", image }), so its error card can offer Try again.
  const [failed, setFailed] = useState(null);
  // Separate from `error` (upload/scan) so the list area can offer its own
  // Retry without an unrelated upload failure clearing it, or vice versa.
  const [loadError, setLoadError] = useState(null);
  // The doc currently shown in the full-screen DocViewer modal, or null when closed.
  const [viewerDoc, setViewerDoc] = useState(null);
  // Link-add form (admin only, gated by `allowLink`).
  const [linkUrl, setLinkUrl] = useState('');
  const [linkType, setLinkType] = useState('wiring_diagram');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkNote, setLinkNote] = useState(null);
  const techSheetInput     = useRef(null);
  const wiringDiagramInput = useRef(null);

  useEffect(() => {
    let ignore = false;
    if (modelNumber) load(modelNumber, () => ignore);
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelNumber]);

  // `isStale` lets a caller (the modelNumber effect) discard a response that
  // arrives after a newer model has superseded it — e.g. a tech corrects a
  // misread model number via tap-to-edit while the old fetch is in flight.
  async function load(model = modelNumber, isStale = () => false) {
    setLoadError(null);
    try {
      const data = await api.listDocumentsForModel(model);
      if (isStale()) return;
      setDocuments(data.documents);
      onDocumentsChange?.(data.documents);
    } catch (e) {
      if (isStale()) return;
      setLoadError(friendlyError(e, 'load saved documents'));
    }
  }

  /**
   * One handler shared by both upload buttons. The doc type is fixed by which
   * button was clicked — the tech doesn't have to pick it from a separate
   * control any more. The /api/documents/upload payload is unchanged.
   */
  async function handleUpload(e, docType) {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-selected later; the File
    // object itself stays usable for a retry.
    if (e.target) e.target.value = '';
    if (!file) return;
    await uploadFile(file, docType);
  }

  // A failed upload or scan keeps what it sent (failed), so Try again re-sends
  // it: the tech doesn't re-pick the PDF or re-shoot the sheet taped inside
  // the kick plate.
  async function uploadFile(file, docType) {
    setError(null);
    setFailed(null);
    setBusy(`upload-${docType}`);
    setStage(0);
    const stageTimer = setInterval(() => {
      setStage((s) => Math.min(s + 1, PROGRESS_UPLOAD.length - 1));
    }, 1500);
    try {
      // PDFs: read the text layer on-device (pdf.js) so the worker can index
      // every page for the troubleshooting AI. Returns [] on any failure and
      // the upload still goes through.
      const pages =
        file.type === 'application/pdf' ? await extractPdfPages(file) : [];
      const created = await api.uploadDocument({
        file,
        modelNumber,
        docType,
        lookupId,
        pages,
      });
      await load();
      // Page images ride AFTER the upload, in the background: the doc is
      // already saved and usable; each page that lands makes the AI able to
      // look at the drawing (and OCRs pages with no text layer).
      if (file.type === 'application/pdf' && created?.id) indexPageImages(created.id, file);
    } catch (err) {
      setFailed({ kind: 'upload', file, docType });
      setError(friendlyError(err, 'upload that file'));
    } finally {
      clearInterval(stageTimer);
      setBusy(null);
    }
  }

  // Render every page of a PDF to a JPEG on the iPad and PUT it to the
  // worker, one at a time, with a small progress line. Never blocks the UI;
  // failures are per page and quiet. `skip` lets a re-run resume.
  const [imageProgress, setImageProgress] = useState(null); // { docId, done, total }
  async function indexPageImages(docId, file, skip = new Set()) {
    setImageProgress({ docId, done: skip.size, total: null });
    let done = skip.size;
    await renderPdfPageImages(file, async (n, blob, total) => {
      try { await api.putDocumentPageImage(docId, n, blob); } catch (e) { console.warn('page image upload failed', n, e); }
      done += 1;
      setImageProgress({ docId, done, total });
    }, { skip });
    setImageProgress(null);
    await load();
  }

  // For a PDF already on file: fetch it back, find which pages still lack an
  // image, and render the rest. This is how the manuals uploaded before
  // migration 0011 (and any curl/portal uploads) get their drawings indexed.
  async function handleIndexImages(doc) {
    if (busy || imageProgress) return;
    setError(null);
    setFailed(null);
    try {
      const status = await api.getDocumentPageImageStatus(doc.id);
      const skip = new Set(status?.pages_with_images || []);
      const res = await fetch(api.documentFileUrl(doc.id));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], doc.file_name || 'manual.pdf', { type: 'application/pdf' });
      await indexPageImages(doc.id, file, skip);
    } catch (e) {
      setImageProgress(null);
      setError(friendlyError(e, 'index the page images'));
    }
  }

  async function handleScanCapture(dataUrl) {
    setShowScanner(false);
    let compressed;
    try {
      // Higher resolution for docs — they need to be readable
      compressed = await compressDataUrl(dataUrl, { maxDimension: 2000, quality: 0.9 });
    } catch (e) {
      setError(friendlyError(e, 'read that photo'));
      return;
    }
    await sendScan(compressed);
  }

  async function sendScan(image) {
    setError(null);
    setFailed(null);
    setBusy('scan');
    setStage(0);
    const stageTimer = setInterval(() => {
      setStage((s) => Math.min(s + 1, PROGRESS_SCAN.length - 1));
    }, 1500);
    try {
      await api.scanDocument({ image, modelNumber, lookupId });
      await load();
    } catch (e) {
      setFailed({ kind: 'scan', image });
      setError(friendlyError(e, 'save the scan'));
    } finally {
      clearInterval(stageTimer);
      setBusy(null);
    }
  }

  async function handleAddLink() {
    const url = linkUrl.trim();
    if (!url || linkBusy || !modelNumber) return;
    setError(null);
    setFailed(null);
    setLinkNote(null);
    setLinkBusy(true);
    try {
      const res = await api.addDocumentLink({ modelNumber, docType: linkType, url });
      setLinkUrl('');
      setLinkNote(
        res.mode === 'indexed'
          ? '✓ Pulled in and indexed — the AI can read this one.'
          : "✓ Saved as an openable link — couldn't read it as a file, so the AI won't use it. Upload the PDF for that."
      );
      await load();
    } catch (e) {
      setError(friendlyError(e, 'add that link'));
    } finally {
      setLinkBusy(false);
    }
  }

  // Reference docs (added by link) store an external URL in file_url and have
  // no R2 object — open those directly; everything else opens in the viewer.
  function openDoc(doc) {
    if (/^https?:\/\//i.test(doc.file_url || '')) {
      window.open(doc.file_url, '_blank', 'noopener');
    } else {
      setViewerDoc(doc);
    }
  }

  const isUploadingTech    = busy === 'upload-tech_sheet';
  const isUploadingWiring  = busy === 'upload-wiring_diagram';
  const isScanning         = busy === 'scan';

  return (
    <>
      {showScanner && (
        <DocumentScanner
          onCapture={handleScanCapture}
          onCancel={() => setShowScanner(false)}
        />
      )}

      {viewerDoc && (
        <DocViewer doc={viewerDoc} onClose={() => setViewerDoc(null)} />
      )}

      <div className="space-y-4">
        <div>
          <h3 className="text-white font-bold text-tap mb-1">Documentation</h3>
          <p className="text-ink-300 text-sm">
            Saved for {modelNumber} — reusable on every job with this model
          </p>
        </div>

        {/* Upload cluster: two side-by-side light-blue buttons in a dark navy frame.
            Each has a cloud-upload SVG icon and a fixed doc type so the tech
            doesn't have to pick from a separate control any more. */}
        <div className="btn-upload-cluster">
          {/* All three add-a-doc affordances are gated on modelNumber, like the
              link form below — a No-Data-Tag scan arrives here with a null
              model, and an upload keyed to it would be filed under the literal
              string "null" and lost the moment the model is corrected. */}
          <button
            onClick={() => techSheetInput.current?.click()}
            disabled={!!busy || !modelNumber}
            className="btn-upload"
            aria-label="Upload Tech Sheet"
          >
            <CloudUploadIcon className="w-6 h-6 shrink-0" />
            <span className="text-left flex-1">
              <span className="block leading-tight">
                {isUploadingTech ? PROGRESS_UPLOAD[stage] : 'Upload Tech Sheet'}
              </span>
              <span className="block text-xs font-normal text-white/70">
                {isUploadingTech ? ' ' : 'PDF or image'}
              </span>
            </span>
          </button>
          <input
            ref={techSheetInput}
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => handleUpload(e, 'tech_sheet')}
            className="hidden"
          />

          <button
            onClick={() => wiringDiagramInput.current?.click()}
            disabled={!!busy || !modelNumber}
            className="btn-upload"
            aria-label="Upload Wiring Diagram"
          >
            <CloudUploadIcon className="w-6 h-6 shrink-0" />
            <span className="text-left flex-1">
              <span className="block leading-tight">
                {isUploadingWiring ? PROGRESS_UPLOAD[stage] : 'Upload Wiring Diagram'}
              </span>
              <span className="block text-xs font-normal text-white/70">
                {isUploadingWiring ? ' ' : 'PDF or image'}
              </span>
            </span>
          </button>
          <input
            ref={wiringDiagramInput}
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => handleUpload(e, 'wiring_diagram')}
            className="hidden"
          />
        </div>

        {/* Scan-on-unit (separate from upload pair — different mode of capture) */}
        <button
          onClick={() => setShowScanner(true)}
          disabled={!!busy || !modelNumber}
          className="btn-secondary w-full justify-start text-left"
        >
          <span className="text-2xl">📷</span>
          <span className="flex-1">
            <span className="block">{isScanning ? PROGRESS_SCAN[stage] : 'Scan On-Unit Doc'}</span>
            <span className="block text-xs font-normal text-ink-300">
              {isScanning ? ' ' : 'tech sheet on appliance'}
            </span>
          </span>
        </button>

        {/* Add-by-link (admin only). A direct PDF/image url is fetched + indexed
            like an upload; anything else is saved as an openable reference. */}
        {allowLink && (
          <div className="card !p-3 bg-ink-900 border-ink-700 space-y-2">
            <div className="text-white text-sm font-semibold">Add by link</div>
            <div className="flex gap-2">
              <select
                value={linkType}
                onChange={(e) => setLinkType(e.target.value)}
                disabled={linkBusy}
                className="bg-ink-800 border border-ink-700 rounded-lg text-white text-sm px-2 py-2 shrink-0"
                aria-label="Document type for the link"
              >
                <option value="wiring_diagram">Wiring Diagram</option>
                <option value="tech_sheet">Tech Sheet</option>
              </select>
              <input
                type="url"
                inputMode="url"
                placeholder="https://…"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                disabled={linkBusy || !modelNumber}
                className="flex-1 min-w-0 bg-ink-800 border border-ink-700 rounded-lg text-white text-sm px-3 py-2 focus:outline-none focus:border-mra-red"
              />
              <button
                type="button"
                onClick={handleAddLink}
                disabled={linkBusy || !linkUrl.trim() || !modelNumber}
                className="btn-primary shrink-0 !py-2"
              >
                {linkBusy ? 'Adding…' : 'Add'}
              </button>
            </div>
            <p className="text-ink-400 text-xs">
              A direct PDF/image link is pulled in and read by the AI. Other links (Drive shares, portal pages) are saved as a tap-to-open reference.
            </p>
            {linkNote && <p className="text-ink-200 text-xs">{linkNote}</p>}
          </div>
        )}

        {error && (
          <div className="card border-fail bg-fail/10">
            <p className="text-white text-sm">⚠ {error}</p>
            {failed && (
              <div className="flex flex-col sm:flex-row gap-3 mt-3">
                <button
                  type="button"
                  disabled={!!busy}
                  className="btn-primary flex-1"
                  onClick={() => (failed.kind === 'scan' ? sendScan(failed.image) : uploadFile(failed.file, failed.docType))}
                >
                  ↻ {failed.kind === 'scan' ? 'Try again with this photo' : `Try again with ${failed.file.name}`}
                </button>
                {failed.kind === 'scan' && (
                  <button
                    type="button"
                    disabled={!!busy}
                    className="btn-secondary flex-1"
                    onClick={() => { setError(null); setFailed(null); setShowScanner(true); }}
                  >
                    📷 Retake photo
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Saved documents list */}
        {!modelNumber && (
          <p className="text-ink-300 text-sm text-center py-4 italic">
            Model number wasn't read, so saved documents can't be looked up —
            fix it above to see or add tech sheets.
          </p>
        )}

        {modelNumber && loadError && (
          <div className="card border-fail bg-fail/10">
            <p className="text-white text-sm mb-3">⚠ {loadError}</p>
            <button
              type="button"
              onClick={() => load(modelNumber)}
              className="btn-secondary w-full"
            >
              Retry
            </button>
          </div>
        )}

        {modelNumber && !loadError && documents === null && (
          <p className="text-ink-300 text-sm text-center py-4">Loading documents…</p>
        )}

        {modelNumber && !loadError && documents?.length === 0 && (
          <>
            <p className="text-ink-300 text-sm text-center py-1 italic">
              No docs yet — upload a tech sheet, scan one off the unit, or find it with Manual Search.
            </p>
            <ManualLibraryLink modelNumber={modelNumber} variant="prominent" />
          </>
        )}

        <div className="space-y-2">
          {documents?.map((doc) => (
            <div key={doc.id}>
              <DocCard
                doc={doc}
                onView={() => openDoc(doc)}
              />
              {/* Page images (migration 0011): lets the AI look at the
                  drawing on wiring questions and OCRs pages with no text
                  layer. Sits beside the row — the row itself is a button. */}
              {isPdfDoc(doc) && (
                <div className="flex items-center justify-between gap-2 px-1 pt-1">
                  {imageProgress?.docId === doc.id ? (
                    <span className="text-ink-400 text-xs">
                      🖼 Indexing page images… {imageProgress.done}{imageProgress.total ? ` of ${imageProgress.total}` : ''}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleIndexImages(doc)}
                      disabled={!!busy || !!imageProgress}
                      className="text-xs text-ink-300 hover:text-white underline underline-offset-2 min-h-[44px] px-1 disabled:opacity-50"
                      title="Render every page to an image so the AI can read wiring diagrams and scanned pages"
                    >
                      🖼 Index page images for the AI
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {modelNumber && !loadError && documents?.length > 0 && (
          <ManualLibraryLink modelNumber={modelNumber} variant="secondary" />
        )}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// One row per saved document. Tapping anywhere on the row opens the in-app
// DocViewer (PDF or image lightbox). Previously the "Open file" link was
// hidden inside an expand-toggle, which buried it past the 3-tap rule.

function isPdfDoc(doc) {
  return doc?.mime_type === 'application/pdf' || /\.pdf$/i.test(doc?.file_name || '');
}

function DocCard({ doc, onView }) {
  const sourceLabel = {
    upload: 'Uploaded',
    scan:   'Scanned from Unit',
    portal: 'From Portal',
  }[doc.source] || doc.source;

  const docTypeLabel = {
    tech_sheet:     'Tech Sheet',
    wiring_diagram: 'Wiring Diagram',
    both:           'Tech Sheet + Diagram',
    other:          'Other',
  }[doc.doc_type] || doc.doc_type;

  const kind = detectKind(doc);
  const viewLabel =
    doc.doc_type === 'wiring_diagram' ? 'View Diagram' :
    doc.doc_type === 'tech_sheet'     ? 'View Tech Sheet' :
    'View';

  return (
    <button
      type="button"
      onClick={onView}
      className="card !p-0 overflow-hidden w-full text-left active:bg-ink-800 transition flex items-stretch gap-0"
      aria-label={`${viewLabel}: ${doc.file_name || 'document'}`}
    >
      <div className="flex-1 min-w-0 p-4">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="badge-source">{sourceLabel}</span>
          <span className="badge bg-mra-red/15 text-mra-red border border-mra-red/30">
            {docTypeLabel}
          </span>
          {doc.has_structured ? (
            <span className="badge-pass">AI Indexed</span>
          ) : doc.has_text ? (
            <span className="badge-marginal">Text Only</span>
          ) : null}
        </div>
        <div className="text-white font-medium truncate">{doc.file_name || 'Untitled doc'}</div>
        <div className="text-ink-300 text-xs">
          {formatBytes(doc.file_size)} · {formatDate(doc.created_at)}{doc.uploaded_by ? ` · ${doc.uploaded_by}` : ''}
        </div>
      </div>

      {/* Right-side View affordance — visible without expanding so a tech
          can open the doc in one tap. The whole card is the click target;
          this just makes the affordance visually obvious. */}
      <div className="shrink-0 flex flex-col items-center justify-center gap-1 px-4 py-3 bg-mra-red/10 border-l border-mra-red/30 min-w-[88px]">
        <ViewIcon kind={kind} />
        <span className="text-mra-red text-xs font-semibold uppercase tracking-wider">View</span>
      </div>
    </button>
  );
}

function ViewIcon({ kind }) {
  // PDF page icon vs image icon — small visual hint at content type.
  if (kind === 'image') {
    return (
      <svg className="w-6 h-6 text-mra-red" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="9" cy="9" r="1.5" />
        <path d="M21 15l-5-5L6 21" />
      </svg>
    );
  }
  return (
    <svg className="w-6 h-6 text-mra-red" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="15" y2="17" />
    </svg>
  );
}

function CloudUploadIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Cloud silhouette */}
      <path d="M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.5 1.5A4 4 0 0 0 6.5 19h11z" />
      {/* Upward arrow */}
      <polyline points="9 14 12 11 15 14" />
      <line x1="12" y1="11" x2="12" y2="20" />
    </svg>
  );
}

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
